import { useState, useCallback } from 'react';
import { message } from 'antd';
import { pwh205Api } from '../../../services/pwh205Api';
import type { FH110, FH111 } from '../../../services/pwh205Api';

export function usePWH205Record() {
  const [record, setRecord] = useState<FH110 | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const data = await pwh205Api.get(id);
      setRecord(data);
      return data;
    } catch (err: unknown) {
      const axErr = err as { response?: { data?: { error?: string } }; message?: string };
      message.error('載入失敗：' + (axErr?.response?.data?.error || axErr?.message || ''));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const save = useCallback(async (
    mode: 'add' | 'edit',
    master: Partial<FH110>,
    details: Partial<FH111>[]
  ) => {
    setSaving(true);
    try {
      let result: FH110;
      if (mode === 'add') {
        result = await pwh205Api.create(master, details);
      } else {
        result = await pwh205Api.update(master.fh110_01!, master, details);
      }
      setRecord(result);
      message.success('存檔成功');
      return result;
    } catch (err: unknown) {
      const axErr = err as { response?: { data?: { errors?: Array<{ message: string }>; error?: string } }; message?: string };
      const apiErr = axErr?.response?.data;
      if (apiErr?.errors) {
        const msgs = apiErr.errors.map(e => e.message).join('；');
        message.error('驗證錯誤：' + msgs);
      } else {
        message.error('存檔失敗：' + (apiErr?.error || axErr?.message || ''));
      }
      return null;
    } finally {
      setSaving(false);
    }
  }, []);

  const remove = useCallback(async (id: string) => {
    setSaving(true);
    try {
      await pwh205Api.delete(id);
      setRecord(null);
      message.success('刪除成功');
      return true;
    } catch (err: unknown) {
      const axErr = err as { response?: { data?: { error?: string } }; message?: string };
      message.error('刪除失敗：' + (axErr?.response?.data?.error || axErr?.message || ''));
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  return { record, loading, saving, load, save, remove, setRecord };
}
